import React from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';

import { formatDateForChat } from '../../../../../chat/utils/time';
import { variants } from '../../../../../chat/components/Tag';

import {
  PreviewContainer,
  ContentContainer,
  TextContainer,
  LeadName,
  MessagePreview,
  TimeStamp,
  InfoContainer,
  StyledTag,
  TagRow,
  CommentMentionHighlight,
  LastContactedInfo,
} from './styles';
import texts from './texts.json';

/*
  A mention raised through the composer is a single lowercase handle:
  ActionItemsContextProvider normalises it to `${firstName}${lastName}` with whitespace stripped
  and lowercased, and handleMentionSelect inserts exactly that, so Sidath Munasinghe is mentioned
  as `@sidathmunasinghe`.

  The pattern accepts capitals anyway. A hand-typed `@Sidath Munasinghe` raises no action item,
  but it is still addressed at someone and a reader scanning the list should see it marked -
  flagging it is a display concern, separate from whether it notified anyone.

  Only the first token is taken, so "@Sidath Munasinghe S2-8705 drawer" marks `@Sidath` and leaves
  the surname and the ticket id alone. Continuing across the space would need to guess where a
  name ends and would swallow "S2-8705". Apostrophes and hyphens are kept because they survive
  normalisation ("Mary-Jane O'Neil" -> `maryjaneo'neil`); a dot is not, so a trailing sentence
  period stays outside the highlight.
*/
const MENTION_REGEX = /@([A-Za-z][A-Za-z0-9'-]*)/g;

const renderMessagePreview = (text, isComment) => {
  if (!isComment) {
    return <MessagePreview>{text}</MessagePreview>;
  }

  const parts = text.split(MENTION_REGEX);
  const nodes = parts.map((part, i) => {
    // Even indices are plain text, odd indices are captured mention names
    if (i % 2 === 1) {
      // eslint-disable-next-line react/no-array-index-key
      return <CommentMentionHighlight key={`mention-${i}`}>{`@${part}`}</CommentMentionHighlight>;
    }
    return part;
  });

  return <MessagePreview $isComment={isComment}>{nodes}</MessagePreview>;
};

const LeadConversationPreview = ({
  name,
  lastMessageText,
  lastMessageAt,
  lastMessageIsComment,
  isActive,
  onClick,
  claimedBy,
  claimedByUsername,
  isClaimValid,
  currentUserId,
  lastOutgoingIsAgentName,
  useAutomatedSdr,
}) => {
  const showClaim = !!claimedBy && isClaimValid;
  const isSelfClaimed = showClaim && claimedBy === currentUserId;

  return (
    <PreviewContainer
      onClick={onClick}
      $isActive={isActive}
    >
      <ContentContainer>
        <TextContainer>
          <LeadName>{name}</LeadName>
          {(showClaim || useAutomatedSdr) && (
            <TagRow>
              {showClaim && (
                <StyledTag variant={isSelfClaimed ? variants.SUCCESS : variants.WARNING}>
                  {isSelfClaimed ? texts.you : format(texts.engagedBy, { name: claimedByUsername || claimedBy })}
                </StyledTag>
              )}
              {/* DANGER, not SUCCESS: matches the detail panel's banner for the same fact, and a
                  self-claimed row renders the claim tag beside it — SUCCESS made them identical. */}
              {useAutomatedSdr && (
                <StyledTag variant={variants.DANGER}>
                  {texts.automatedSdr}
                </StyledTag>
              )}
            </TagRow>
          )}
          {renderMessagePreview(lastMessageText, lastMessageIsComment)}
          {!!lastOutgoingIsAgentName && (
            <LastContactedInfo>
              {format(texts.lastBy, { name: lastOutgoingIsAgentName })}
            </LastContactedInfo>
          )}
        </TextContainer>
        <InfoContainer>
          {!!lastMessageAt && (
            <TimeStamp $isActive={isActive}>
              {formatDateForChat(lastMessageAt, true)}
            </TimeStamp>
          )}
        </InfoContainer>
      </ContentContainer>
    </PreviewContainer>
  );
};

LeadConversationPreview.propTypes = {
  name: PropTypes.string.isRequired,
  lastMessageText: PropTypes.string,
  lastMessageAt: PropTypes.instanceOf(Date),
  lastMessageIsComment: PropTypes.bool,
  isActive: PropTypes.bool,
  onClick: PropTypes.func,
  claimedBy: PropTypes.string,
  claimedByUsername: PropTypes.string,
  isClaimValid: PropTypes.bool,
  currentUserId: PropTypes.string,
  lastOutgoingIsAgentName: PropTypes.string,
  useAutomatedSdr: PropTypes.bool,
};

LeadConversationPreview.defaultProps = {
  lastMessageText: '',
  lastMessageAt: null,
  lastMessageIsComment: false,
  isActive: false,
  onClick: () => {},
  claimedBy: undefined,
  claimedByUsername: undefined,
  isClaimValid: false,
  currentUserId: undefined,
  lastOutgoingIsAgentName: undefined,
  useAutomatedSdr: false,
};

export default LeadConversationPreview;
